import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PROFILES, getLevelLabel } from "@/lib/constants";
import { Baby, HeartHandshake, Activity, HeartPulse, ShieldCheck } from "lucide-react";

const iconMap = {
  Baby, HeartHandshake, Activity, HeartPulse, ShieldCheck,
};

const MockUI = ({ profile }) => {
  const bars = [
    { label: "Spice", value: profile.settings.spice, color: "#9F3E18" },
    { label: "Salt", value: profile.settings.salt, color: "#2E5C5A" },
    { label: "Oil", value: profile.settings.oil, color: "#D97706" },
    { label: "Sweetness", value: profile.settings.sweetness, color: "#C8A951" },
  ];

  return (
    <motion.div
      key={profile.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-3xl p-6 lg:p-8 border border-gray-100 shadow-sm"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${profile.color}15` }}>
          {(() => {
            const Icon = iconMap[profile.icon];
            return Icon ? <Icon size={18} style={{ color: profile.color }} /> : null;
          })()}
        </div>
        <div>
          <h4 className="font-heading font-700 text-sw-text">{profile.name}</h4>
          <p className="text-xs text-sw-text-muted">{profile.nutritionNote}</p>
        </div>
      </div>

      <div className="space-y-5">
        {bars.map((bar) => (
          <div key={bar.label}>
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-sm font-medium text-sw-text-secondary">{bar.label}</span>
              <span className="text-xs text-sw-text-muted">{getLevelLabel(bar.value)}</span>
            </div>
            <div className="h-2 bg-cream-tertiary rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${bar.value}%` }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="h-full rounded-full"
                style={{ backgroundColor: bar.color }}
              />
            </div>
          </div>
        ))}
      </div>

      {profile.excludedIngredients.length > 0 && (
        <div className="mt-6 pt-5 border-t border-gray-100">
          <span className="text-xs font-medium text-sw-text-muted uppercase tracking-wider">Excluded</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {profile.excludedIngredients.map((ing) => (
              <span key={ing} className="text-xs bg-red-50 text-spice-terracotta px-2.5 py-1 rounded-full">
                {ing}
              </span>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export const SmartProfiles = () => {
  const [activeProfile, setActiveProfile] = useState(PROFILES[0]);

  return (
    <section id="smart-profiles" data-testid="smart-profiles-section" className="py-24 lg:py-36 bg-cream-secondary/40">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="text-sm font-medium text-earth-green uppercase tracking-wider">Smart Profiles</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-700 text-sw-text mt-4 leading-tight">
            One family, <span className="font-accent italic text-earth-green">different needs</span>
          </h2>
          <p className="text-base text-sw-text-secondary mt-4">
            Select a profile, and watch the recipe adapt automatically.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          {/* Profile Cards */}
          <div className="space-y-3">
            {PROFILES.map((profile, index) => {
              const Icon = iconMap[profile.icon];
              const isActive = activeProfile.id === profile.id;
              return (
                <motion.button
                  key={profile.id}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`profile-card-${profile.id}`}
                  onClick={() => setActiveProfile(profile)}
                  className={`w-full text-left p-4 lg:p-5 rounded-2xl border transition-all duration-300 ${
                    isActive
                      ? "profile-card-active bg-white border-earth-green"
                      : "bg-white/60 border-gray-100 hover:bg-white hover:border-gray-200"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${profile.color}12` }}
                    >
                      {Icon && <Icon size={20} style={{ color: profile.color }} />}
                    </div>
                    <div className="min-w-0">
                      <h4 className="font-heading font-600 text-sw-text text-sm lg:text-base">{profile.name}</h4>
                      <p className="text-xs text-sw-text-muted">{profile.description}</p>
                    </div>
                    <div className={`ml-auto w-2 h-2 rounded-full shrink-0 transition-colors duration-300 ${isActive ? "bg-earth-green" : "bg-cream-tertiary"}`} />
                  </div>
                </motion.button>
              );
            })}
          </div>

          {/* Mock UI Panel */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="sticky top-28"
          >
            <AnimatePresence mode="wait">
              <MockUI profile={activeProfile} />
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
